import { useEffect, useState, useRef } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { api, PRODUCT_CATEGORIES, formatTL, resolveImage, DELIVERY_PLATFORMS } from "@/lib/api";
import { ProductFormModal } from "@/components/app/ProductFormModal";
import { OrderBoard } from "@/components/app/OrderBoard";
import { CustomersPanel } from "@/components/app/CustomersPanel";
import { DeliveryOrderPanel } from "@/components/app/DeliveryOrderPanel";
import { IntegrationsPanel, NetgsmSmsCard } from "@/components/app/IntegrationsPanel";
import { PrinterSettingsCard } from "@/components/app/PrinterSettingsCard";
import { printPriceList } from "@/lib/priceList";
import { DiscountCodesPanel } from "@/components/app/DiscountCodesPanel";
import { KioskPinCard } from "@/components/app/KioskPinCard";
import { CategoriesPanel } from "@/components/app/CategoriesPanel";
import { BranchesPanel } from "@/components/app/BranchesPanel";
import { UsersPanel } from "@/components/app/UsersPanel";
import { AdminsPanel } from "@/components/app/AdminsPanel";
import { ReportsPanel } from "@/components/app/ReportsPanel";
import { Plus, Pencil, Trash2, TrendingUp, ShoppingBag, Utensils, Search, Users, Truck, Plug, GripVertical, Tag, LogOut, BarChart3, Store, UserCog, Power, Percent, Copy, MessageSquare, Link as LinkIcon, ChevronDown, Printer } from "lucide-react";
import { toast } from "sonner";

const ProductRow = ({ product: p, onEdit, onDelete, onToggle, onDragEnd }) => {
  const controls = useDragControls();
  const passive = p.active === false;
  return (
    <Reorder.Item
      as="div"
      value={p}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      data-testid={`admin-product-${p.id}`}
      className={`flex items-center gap-3 p-3 sm:p-4 bg-white hover:bg-[var(--fb-offwhite)] ${passive ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        data-testid={`drag-handle-${p.id}`}
        onPointerDown={(e) => controls.start(e)}
        className="cursor-grab active:cursor-grabbing touch-none text-[var(--fb-muted)] hover:text-[var(--fb-ink)] shrink-0"
      >
        <GripVertical className="w-5 h-5" />
      </button>
      <div className="w-14 h-14 rounded-xl bg-[var(--fb-neutral)] overflow-hidden shrink-0">
        {p.image_url && (
          <img src={resolveImage(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold truncate">
          {p.name}
          {passive && (
            <span data-testid={`product-passive-badge-${p.id}`} className="ml-2 text-[10px] font-bold bg-red-50 text-[var(--fb-red)] px-2 py-0.5 rounded-full align-middle">
              Pasif
            </span>
          )}
        </p>
        <span className="inline-block text-xs bg-[var(--fb-neutral)] text-[var(--fb-muted)] px-2 py-0.5 rounded-full mt-1">
          {p.category}
        </span>
      </div>
      <div className="font-display font-bold text-[var(--fb-red)] whitespace-nowrap">
        {formatTL(p.price)}
      </div>
      <div className="flex gap-1">
        <button
          data-testid={`toggle-product-${p.id}`}
          onClick={() => onToggle(p)}
          title={passive ? "Aktif et" : "Pasife al"}
          className={`w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] ${passive ? "text-[var(--fb-muted)]" : "text-green-600"}`}
        >
          <Power className="w-4 h-4" />
        </button>
        <button
          data-testid={`edit-product-${p.id}`}
          onClick={() => onEdit(p)}
          className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          data-testid={`delete-product-${p.id}`}
          onClick={() => onDelete(p.id)}
          className="w-9 h-9 grid place-items-center rounded-full hover:bg-red-50 text-[var(--fb-red)]"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </Reorder.Item>
  );
};

export default function Admin({ user, onLogout }) {
  const [tab, setTab] = useState(user?.role === "superadmin" ? "products" : "orders");
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({ revenue: 0, order_count: 0, active_products: 0, customer_count: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("Tümü");
  const [search, setSearch] = useState("");
  const [cats, setCats] = useState(PRODUCT_CATEGORIES);
  const [revPeriod, setRevPeriod] = useState("today");
  const [statsOpen, setStatsOpen] = useState(() => localStorage.getItem("fb_stats_open") === "1");
  const toggleStats = () =>
    setStatsOpen((v) => {
      localStorage.setItem("fb_stats_open", v ? "0" : "1");
      if (v) setLinkInfoOpen(false);
      return !v;
    });
  const [range, setRange] = useState({ start: "", end: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [linkInfoOpen, setLinkInfoOpen] = useState(false);
  const [bulkPlatform, setBulkPlatform] = useState("all");
  const [bulkPercent, setBulkPercent] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const applyBulkPricing = async () => {
    const pct = parseFloat(bulkPercent);
    if (isNaN(pct)) {
      toast.error("Geçerli bir yüzde girin");
      return;
    }
    setBulkSaving(true);
    try {
      const res = await api.post("/products/bulk-platform-pricing", { platform: bulkPlatform, percent: pct });
      toast.success(`${res.data.updated} ürüne platform fiyatı uygulandı`);
      setBulkOpen(false);
      setBulkPercent("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
    setBulkSaving(false);
  };

  const orderLink = user?.branch_code ? `${window.location.origin}/?sube=${user.branch_code}` : "";
  const copyOrderLink = () => {
    navigator.clipboard?.writeText(orderLink);
    toast.success("Sipariş linki kopyalandı");
  };

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
      setProducts(p.data);
      setCats(c.data.map((x) => x.name));
    } catch (e) {
      toast.error("Veriler yüklenemedi");
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const fetchStats = () => {
    const now = new Date();
    let params = {};
    if (revPeriod === "today") {
      params = {
        start: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(),
        end: now.toISOString(),
      };
    } else if (revPeriod === "month") {
      params = {
        start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
        end: now.toISOString(),
      };
    } else {
      if (range.start) params.start = new Date(range.start + "T00:00:00").toISOString();
      if (range.end) params.end = new Date(range.end + "T23:59:59").toISOString();
    }
    api
      .get("/stats", { params })
      .then((r) => setStats(r.data))
      .catch(() => {});
    };
    fetchStats();
    const t = setInterval(fetchStats, 15000);
    return () => clearInterval(t);
  }, [revPeriod, range]);

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Ürün silindi");
      load();
    } catch (e) {
      toast.error("Silme başarısız");
    }
  };

  const toggleActive = async (p) => {
    const next = p.active === false;
    try {
      await api.put(`/products/${p.id}/active`, { active: next });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: next } : x)));
      toast.success(next ? "Ürün aktif edildi" : "Ürün pasife alındı");
    } catch (e) {
      toast.error("Durum güncellenemedi");
    }
  };

  const onSaved = () => {
    setModalOpen(false);
    load();
  };

  const productsRef = useRef(products);
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  const canReorder = filterCat === "Tümü" && !search;

  const persistOrder = async (list) => {
    try {
      await api.put("/products/reorder", { ids: list.map((p) => p.id) });
      toast.success("Sıralama kaydedildi");
    } catch (e) {
      toast.error("Sıralama kaydedilemedi");
    }
  };

  const filtered = products.filter((p) => {
    const catOk = filterCat === "Tümü" || p.category === filterCat;
    const searchOk = p.name.toLowerCase().includes(search.toLowerCase());
    return catOk && searchOk;
  });

  const periodLabel =
    revPeriod === "today" ? "Bugünkü Ciro" : revPeriod === "month" ? "Bu Ay Cirosu" : "Seçili Aralık Cirosu";
  const orderLabel =
    revPeriod === "today" ? "Bugünkü Sipariş" : revPeriod === "month" ? "Bu Ay Siparişi" : "Aralık Siparişi";

  const statCards = [
    { label: periodLabel, value: formatTL(stats.revenue), icon: TrendingUp, color: "text-[var(--fb-red)]" },
    { label: orderLabel, value: stats.order_count, icon: ShoppingBag, color: "text-[var(--fb-amber)]" },
    { label: "Aktif Menü Ürünleri", value: stats.active_products, icon: Utensils, color: "text-green-600" },
    { label: "Kayıtlı Müşteri", value: stats.customer_count, icon: Users, color: "text-blue-600" },
  ].filter((c) => !(user?.role === "superadmin" && (c.label === periodLabel || c.label === orderLabel || c.label === "Kayıtlı Müşteri")));

  const isSuper = user?.role === "superadmin";

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[var(--fb-neutral)]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Kullanıcı barı */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="text-sm text-[var(--fb-muted)]">
            Hoş geldiniz, <span className="font-semibold text-[var(--fb-ink)]">{user?.name || user?.phone}</span>
            {isSuper ? (
              <span className="ml-1 text-xs bg-[var(--fb-ink)] text-white px-2 py-0.5 rounded-full font-semibold">Patron</span>
            ) : (
              <span className="ml-1 text-xs bg-red-50 text-[var(--fb-red)] px-2 py-0.5 rounded-full font-semibold">Şube</span>
            )}
          </p>
          <div className="flex items-center gap-2">
          <button
            data-testid="price-list-btn"
            onClick={() => {
              if (!printPriceList(products, cats)) toast.error("Açılır pencere engellendi, lütfen izin verin");
            }}
            className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-red)] border border-[var(--fb-border)] hover:border-[var(--fb-red)] px-4 py-2 rounded-full transition-colors"
          >
            <Printer className="w-4 h-4" /> Fiyat Listesi
          </button>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-red)] border border-[var(--fb-border)] hover:border-[var(--fb-red)] px-4 py-2 rounded-full transition-colors"
          >
            <LogOut className="w-4 h-4" /> Çıkış Yap
          </button>
          </div>
        </div>


        {bulkOpen && (
          <div className="fixed inset-0 z-[60] bg-black/40 grid place-items-center px-4" onClick={() => setBulkOpen(false)}>
            <div
              data-testid="bulk-platform-modal"
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl border border-[var(--fb-border)] p-6 animate-fade-up"
            >
              <h2 className="font-display font-extrabold text-xl flex items-center gap-2 mb-1">
                <Percent className="w-5 h-5 text-[var(--fb-red)]" /> Toplu Platform Fiyatı
              </h2>
              <p className="text-sm text-[var(--fb-muted)] mb-4">
                Tüm ürünlerin kiosk fiyatına seçilen yüzde eklenerek platform fiyatı olarak uygulanır. Kiosk fiyatı değişmez.
              </p>

              <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Platform</label>
              <select
                data-testid="bulk-platform-select"
                value={bulkPlatform}
                onChange={(e) => setBulkPlatform(e.target.value)}
                className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)] bg-white"
              >
                <option value="all">Tüm Platformlar</option>
                {DELIVERY_PLATFORMS.map((pl) => (
                  <option key={pl.key} value={pl.key}>
                    {pl.label}
                  </option>
                ))}
              </select>

              <label className="mt-3 block text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                Zam Oranı (%)
              </label>
              <input
                data-testid="bulk-percent-input"
                type="number"
                step="0.1"
                value={bulkPercent}
                onChange={(e) => setBulkPercent(e.target.value)}
                placeholder="Örn. 20"
                className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />

              <div className="flex gap-2 mt-5">
                <button
                  data-testid="bulk-cancel-btn"
                  onClick={() => setBulkOpen(false)}
                  className="flex-1 border border-[var(--fb-border)] hover:border-[var(--fb-red)] text-[var(--fb-muted)] hover:text-[var(--fb-red)] font-semibold py-3 rounded-full transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  data-testid="bulk-apply-btn"
                  onClick={applyBulkPricing}
                  disabled={bulkSaving}
                  className="flex-1 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-3 rounded-full transition-colors active:scale-[0.99]"
                >
                  {bulkSaving ? "Uygulanıyor..." : "Uygula"}
                </button>
              </div>
            </div>
          </div>
        )}

        {!isSuper && linkInfoOpen && (
          <div className="mb-4 space-y-3">
            <div
              data-testid="branch-kiosk-banner"
              className="bg-white border border-[var(--fb-border)] rounded-2xl p-4 flex items-center gap-3 flex-wrap"
            >
              <Store className="w-5 h-5 text-[var(--fb-red)]" />
              <span className="text-sm text-[var(--fb-muted)]">
                Kiosk'u açmak için sağ üstteki <span className="font-semibold text-[var(--fb-ink)]">Kiosk'u Aç</span> butonunu kullanın · menü tüm şubelerde ortaktır
              </span>
            </div>
            {user?.branch_code && (
              <div
                data-testid="branch-order-link-banner"
                className="bg-white border border-[var(--fb-border)] rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="flex items-center gap-2 text-sm min-w-0">
                  <LinkIcon className="w-5 h-5 text-[var(--fb-red)] shrink-0" />
                  <span className="text-[var(--fb-muted)] shrink-0">Online sipariş linki:</span>
                  <a
                    href={orderLink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="owner-order-link"
                    className="font-semibold text-[var(--fb-ink)] truncate hover:text-[var(--fb-red)]"
                  >
                    {orderLink}
                  </a>
                </div>
                <button
                  data-testid="copy-order-link-btn"
                  onClick={copyOrderLink}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[var(--fb-muted)] hover:text-[var(--fb-red)] border border-[var(--fb-border)] hover:border-[var(--fb-red)] px-4 py-2 rounded-full transition-colors shrink-0"
                >
                  <Copy className="w-4 h-4" /> Kopyala
                </button>
              </div>
            )}
          </div>
        )}

        {tab !== "reports" && (
          <>
        <button
          data-testid="toggle-stats-btn"
          onClick={toggleStats}
          className="w-full flex items-center justify-between gap-3 bg-white border border-[var(--fb-border)] rounded-2xl px-5 py-3 mb-4 hover:border-[var(--fb-red)] transition-colors"
        >
          <span className="flex items-center gap-2 font-display font-bold">
            <TrendingUp className="w-5 h-5 text-[var(--fb-red)]" /> Özet & Ciro
            {!statsOpen && statCards[0] && (
              <span data-testid="stats-collapsed-summary" className="text-sm font-semibold text-[var(--fb-muted)]">· {statCards[0].label}: {statCards[0].value}</span>
            )}
          </span>
          <ChevronDown className={`w-5 h-5 transition-transform ${statsOpen ? "rotate-180" : ""}`} />
        </button>
        {statsOpen && (
          <div data-testid="stats-section" className="animate-fade-up">
        {/* Ciro dönem seçici */}
        {!isSuper && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex gap-1 bg-white rounded-full p-1 border border-[var(--fb-border)]">
            {[
              ["today", "Günlük"],
              ["month", "Aylık"],
              ["range", "Tarih Aralığı"],
            ].map(([v, l]) => (
              <button
                key={v}
                data-testid={`rev-period-${v}`}
                onClick={() => setRevPeriod(v)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  revPeriod === v ? "bg-[var(--fb-red)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="toggle-link-info-btn"
            onClick={() => setLinkInfoOpen((v) => !v)}
            className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-full border transition-colors ${
              linkInfoOpen
                ? "border-[var(--fb-red)] text-[var(--fb-red)] bg-red-50"
                : "border-[var(--fb-border)] text-[var(--fb-muted)] hover:border-[var(--fb-red)] hover:text-[var(--fb-red)]"
            }`}
          >
            <LinkIcon className="w-4 h-4" /> Kiosk & Sipariş Linki
          </button>
          {revPeriod === "range" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                data-testid="rev-start"
                value={range.start}
                onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
                className="bg-white border border-[var(--fb-border)] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[var(--fb-red)]"
              />
              <span className="text-[var(--fb-muted)]">—</span>
              <input
                type="date"
                data-testid="rev-end"
                value={range.end}
                onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
                className="bg-white border border-[var(--fb-border)] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[var(--fb-red)]"
              />
            </div>
          )}
        </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statCards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.label}
                className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 flex items-center justify-between"
              >
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                    {c.label}
                  </p>
                  <p className="font-display font-extrabold text-2xl mt-1">{c.value}</p>
                </div>
                <Icon className={`w-9 h-9 ${c.color}`} />
              </div>
            );
          })}
        </div>
          </div>
        )}
          </>
        )}

        {/* Tabs + add */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex gap-1 bg-white rounded-full p-1 border border-[var(--fb-border)] overflow-x-auto no-scrollbar max-w-full [&>button]:shrink-0">
            {isSuper && (
              <button
                data-testid="admin-tab-products"
                onClick={() => setTab("products")}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "products" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                Ürünler
              </button>
            )}
            {isSuper && (
              <button
                data-testid="admin-tab-categories"
                onClick={() => setTab("categories")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "categories" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <Tag className="w-4 h-4" /> Kategoriler
              </button>
            )}
            {isSuper && (
              <button
                data-testid="admin-tab-branches"
                onClick={() => setTab("branches")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "branches" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <Store className="w-4 h-4" /> Şubeler
              </button>
            )}
            {isSuper && (
              <button
                data-testid="admin-tab-users"
                onClick={() => setTab("users")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "users" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <UserCog className="w-4 h-4" /> Kullanıcılar
              </button>
            )}
            {isSuper && (
              <button
                data-testid="admin-tab-sms"
                onClick={() => setTab("sms")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "sms" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <MessageSquare className="w-4 h-4" /> SMS
              </button>
            )}
            {!isSuper && (
              <button
                data-testid="admin-tab-orders"
                onClick={() => setTab("orders")}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "orders" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                Siparişler
              </button>
            )}
            {!isSuper && (
              <button
                data-testid="admin-tab-customers"
                onClick={() => setTab("customers")}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "customers" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                Müşteriler
              </button>
            )}
            {!isSuper && (
              <button
                data-testid="admin-tab-delivery"
                onClick={() => setTab("delivery")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "delivery" ? "bg-[var(--fb-red)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <Truck className="w-4 h-4" /> Paket Servis
              </button>
            )}
            {!isSuper && (
              <button
                data-testid="admin-tab-integrations"
                onClick={() => setTab("integrations")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "integrations" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <Plug className="w-4 h-4" /> Entegrasyonlar
              </button>
            )}
            {!isSuper && (
              <button
                data-testid="admin-tab-discounts"
                onClick={() => setTab("discounts")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "discounts" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <Tag className="w-4 h-4" /> İndirim Kodları
              </button>
            )}
            {!isSuper && (
              <button
                data-testid="admin-tab-reports"
                onClick={() => setTab("reports")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "reports" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <BarChart3 className="w-4 h-4" /> Raporlar
              </button>
            )}
            {!isSuper && (
              <button
                data-testid="admin-tab-users"
                onClick={() => setTab("users")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === "users" ? "bg-[var(--fb-ink)] text-white" : "text-[var(--fb-muted)]"
                }`}
              >
                <UserCog className="w-4 h-4" /> Kullanıcılar
              </button>
            )}
          </div>

          {tab === "products" && (
            <div className="flex items-center gap-2">
              <button
                data-testid="bulk-platform-pricing-btn"
                onClick={() => setBulkOpen(true)}
                className="flex items-center gap-2 bg-white border border-[var(--fb-border)] hover:border-[var(--fb-red)] hover:text-[var(--fb-red)] text-[var(--fb-muted)] font-semibold px-4 py-2.5 rounded-full transition-colors active:scale-95"
              >
                <Percent className="w-4 h-4" /> Toplu Platform Fiyatı
              </button>
              <button
                data-testid="add-product-btn"
                onClick={openNew}
                className="flex items-center gap-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold px-5 py-2.5 rounded-full transition-colors active:scale-95"
              >
                <Plus className="w-5 h-5" /> Yeni Ürün Ekle
              </button>
            </div>
          )}
        </div>

        {tab === "products" ? (
          <>
            {/* filters */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
                <input
                  data-testid="product-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ürün ara..."
                  className="w-full bg-white border border-[var(--fb-border)] rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[var(--fb-red)]"
                />
              </div>
              <select
                data-testid="product-filter-cat"
                value={filterCat}
                onChange={(e) => setFilterCat(e.target.value)}
                className="bg-white border border-[var(--fb-border)] rounded-full px-4 py-2 text-sm outline-none focus:border-[var(--fb-red)]"
              >
                <option value="Tümü">Tüm Kategoriler</option>
                {cats.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* product table */}
            <div className="bg-white rounded-2xl border border-[var(--fb-border)] overflow-hidden">
              {filtered.length === 0 ? (
                <div data-testid="admin-empty" className="p-10 text-center text-[var(--fb-muted)]">
                  Ürün bulunamadı. "Yeni Ürün Ekle" ile başlayın.
                </div>
              ) : canReorder ? (
                <>
                  <div className="px-4 py-2 text-xs text-[var(--fb-muted)] bg-[var(--fb-offwhite)] border-b border-[var(--fb-border)] flex items-center gap-1.5">
                    <GripVertical className="w-3.5 h-3.5" /> Ürünleri sürükleyip bırakarak sıralayın
                  </div>
                  <Reorder.Group
                    as="div"
                    axis="y"
                    values={products}
                    onReorder={setProducts}
                    translate="no"
                    className="divide-y divide-[var(--fb-border)] notranslate"
                  >
                    {products.map((p) => (
                      <ProductRow
                        key={p.id}
                        product={p}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        onToggle={toggleActive}
                        onDragEnd={() => persistOrder(productsRef.current)}
                      />
                    ))}
                  </Reorder.Group>
                </>
              ) : (
                <div className="divide-y divide-[var(--fb-border)]">
                  {filtered.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`admin-product-${p.id}`}
                      className={`flex items-center gap-4 p-3 sm:p-4 hover:bg-[var(--fb-offwhite)] ${p.active === false ? "opacity-60" : ""}`}
                    >
                      <div className="w-14 h-14 rounded-xl bg-[var(--fb-neutral)] overflow-hidden shrink-0">
                        {p.image_url && (
                          <img
                            src={resolveImage(p.image_url)}
                            alt={p.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">
                          {p.name}
                          {p.active === false && (
                            <span data-testid={`product-passive-badge-${p.id}`} className="ml-2 text-[10px] font-bold bg-red-50 text-[var(--fb-red)] px-2 py-0.5 rounded-full align-middle">
                              Pasif
                            </span>
                          )}
                        </p>
                        <span className="inline-block text-xs bg-[var(--fb-neutral)] text-[var(--fb-muted)] px-2 py-0.5 rounded-full mt-1">
                          {p.category}
                        </span>
                      </div>
                      <div className="font-display font-bold text-[var(--fb-red)] whitespace-nowrap">
                        {formatTL(p.price)}
                      </div>
                      <div className="flex gap-1">
                        <button
                          data-testid={`toggle-product-${p.id}`}
                          onClick={() => toggleActive(p)}
                          title={p.active === false ? "Aktif et" : "Pasife al"}
                          className={`w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] ${p.active === false ? "text-[var(--fb-muted)]" : "text-green-600"}`}
                        >
                          <Power className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`edit-product-${p.id}`}
                          onClick={() => openEdit(p)}
                          className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-product-${p.id}`}
                          onClick={() => handleDelete(p.id)}
                          className="w-9 h-9 grid place-items-center rounded-full hover:bg-red-50 text-[var(--fb-red)]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : tab === "orders" ? (
          <OrderBoard />
        ) : tab === "customers" ? (
          <CustomersPanel />
        ) : tab === "delivery" ? (
          <DeliveryOrderPanel onPlaced={load} />
        ) : tab === "integrations" ? (
          <>
            {!isSuper && <KioskPinCard />}
            <PrinterSettingsCard />
            <IntegrationsPanel />
          </>
        ) : tab === "discounts" ? (
          <DiscountCodesPanel />
        ) : tab === "reports" ? (
          <ReportsPanel />
        ) : tab === "users" ? (
          isSuper ? <AdminsPanel currentUser={user} /> : <UsersPanel currentUser={user} />
        ) : tab === "sms" ? (
          <NetgsmSmsCard />
        ) : tab === "branches" ? (
          <BranchesPanel />
        ) : (
          <CategoriesPanel onChange={load} />
        )}
      </div>

      <ProductFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
        editing={editing}
      />
    </div>
  );
}
